import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import Navbar from "./components/navbar.tsx";
import Home from "./pages/Home.tsx";
import Loader from "./components/Loader.tsx";
import Footer from "./components/footer.tsx";
import ConsentBanner from "./components/ConsentBanner";
import { AnalyticsConsent, applyConsent, readConsent } from "./lib/analytics";
import { shouldPlayLoader } from "./lib/loader";
import { startSmoothScroll } from "./lib/smoothScroll";
import { usePath } from "./lib/router";
import { projectsList } from "./data/projects";
import { projectContent } from "./data/projectPages";

// A project page is only needed once someone opens one.
const ProjectPage = lazy(() => import("./pages/ProjectPage.tsx"));

/* /projecten/<id> for a project that exists; anything else is the home page. */
const projectIdFor = (path: string) => {
    const match = /^\/projecten\/([^/]+)$/.exec(path);
    return match && projectsList.some((p) => p.id === match[1]) && projectContent[match[1]] ? match[1] : null;
};

const App = () => {
    const [analyticsConsent, setAnalyticsConsent] = useState<AnalyticsConsent>(readConsent());
    const [playLoader] = useState(shouldPlayLoader);
    const [showLoader, setShowLoader] = useState(playLoader);
    // The hero headline waits for the loader to start lifting.
    const [ready, setReady] = useState(!playLoader);

    const path = usePath();
    const projectId = projectIdFor(path);

    useEffect(() => startSmoothScroll(), []);

    const handleReveal = useCallback(() => setReady(true), []);
    const handleLoaderDone = useCallback(() => {
        delete document.documentElement.dataset.boot;
        setShowLoader(false);
    }, []);

    const handleConsentChange = (consent: AnalyticsConsent) => {
        setAnalyticsConsent(consent);
        applyConsent(consent);
    };

    return (
        <MotionConfig reducedMotion="user">
            {showLoader && <Loader onReveal={handleReveal} onDone={handleLoaderDone} />}
            <Navbar />
            <main id="main" data-menu-inert>
                {projectId ? (
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                        <ProjectPage key={projectId} id={projectId} />
                    </Suspense>
                ) : (
                    <Home ready={ready} />
                )}
            </main>
            <Footer />
            <ConsentBanner consent={analyticsConsent} onConsentChange={handleConsentChange} deferred={showLoader} />
        </MotionConfig>
    );
};

export default App;
